import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import type { ReviewSignal, ReviewSignalBundle } from '../../lib/employment/reviewSignalBundle.types';
import { mutationSeverityLabel } from '../../lib/employment/employmentForwardCopy';
import {
  GIG_PLATFORM_OPS_SUMMARY,
  reviewSignalsCountLabel,
  shouldShowGigPlatformOpsSummary,
  splitBundleForDisplay,
} from '../../lib/employment/reviewSignalPresentation';

type Props = {
  bundle: ReviewSignalBundle | null;
  attemptNumber?: number;
  variant?: 'compact' | 'full';
  grouped?: boolean;
  hideHeaderChips?: boolean;
  lastActionResult?: Record<string, unknown> | null;
};

function SignalRows({ signals }: { signals: ReviewSignal[] }) {
  if (signals.length === 0) return null;

  return (
    <Stack spacing={1}>
      {signals.map((signal) => (
        <Stack key={signal.id || `${signal.gate}-${signal.step}-${signal.message}`} spacing={0.35}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" label={signal.gate} variant="outlined" />
            <Chip size="small" label={signal.step} />
            {signal.severity === 'ADVISORY' ? (
              <Chip size="small" color="info" label={mutationSeverityLabel('advisory')} />
            ) : null}
          </Stack>
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            {signal.message}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function SignalAccordion({
  title,
  signals,
  defaultExpanded = false,
}: {
  title: string;
  signals: ReviewSignal[];
  defaultExpanded?: boolean;
}) {
  if (signals.length === 0) return null;

  if (defaultExpanded) {
    return (
      <Stack spacing={0.75}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <SignalRows signals={signals} />
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Accordion
        disableGutters
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title} ({signals.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SignalRows signals={signals} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export function ReviewSignalBundlePanel({
  bundle,
  attemptNumber,
  variant = 'full',
  grouped = true,
  hideHeaderChips = false,
  lastActionResult,
}: Props) {
  if (!bundle) {
    return (
      <Alert severity="info" variant="outlined" data-testid="employment-review-signals-empty">
        No structured review signals are stored for this run.
      </Alert>
    );
  }

  const { failures, advisory, gig, legitimacy, structural, other } = splitBundleForDisplay(bundle);
  const reviewItems = bundle.blockingSignals?.filter((s) => s.status !== 'FAIL') ?? [];
  const countLabels = reviewSignalsCountLabel(bundle.blockingSignals ?? [], advisory);
  const compact = variant === 'compact';
  const structuralAndOther = [...structural, ...other];
  const showGigBanner = shouldShowGigPlatformOpsSummary(bundle, { lastActionResult });

  return (
    <Stack spacing={1.25} data-testid="employment-review-signals-panel">
      {!hideHeaderChips ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {attemptNumber != null ? <Chip size="small" label={`Attempt ${attemptNumber}`} /> : null}
          {bundle.finalStatus ? <Chip size="small" label={bundle.finalStatus} color="warning" /> : null}
          <Chip
            size="small"
            color={failures.length > 0 ? 'error' : reviewItems.length > 0 ? 'warning' : 'default'}
            label={countLabels.reviewLabel}
            data-testid="employment-review-signals-blocking"
          />
          {advisory.length > 0 ? (
            <Chip size="small" label={countLabels.advisoryLabel} data-testid="employment-review-signals-advisory" />
          ) : null}
        </Stack>
      ) : null}

      {grouped ? (
        <>
          <SignalAccordion
            title="Failures requiring action"
            signals={failures}
            defaultExpanded={failures.length > 0 && (compact || failures.length <= 2)}
          />

          {showGigBanner ? (
            <Stack spacing={1}>
              <Alert severity="info" variant="outlined" sx={{ py: 0.75 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                  {GIG_PLATFORM_OPS_SUMMARY}
                </Typography>
              </Alert>
              {gig.length === 1 ? (
                <SignalRows signals={gig} />
              ) : (
                <SignalAccordion title="Platform review details" signals={gig} defaultExpanded={false} />
              )}
            </Stack>
          ) : null}

          {!showGigBanner && gig.length > 0 ? (
            <SignalAccordion title="Platform review details" signals={gig} defaultExpanded={false} />
          ) : null}

          {legitimacy.length > 0 ? (
            <SignalAccordion
              title="Existence and legitimacy review"
              signals={legitimacy}
              defaultExpanded={!compact && legitimacy.length <= 2}
            />
          ) : null}

          {structuralAndOther.length > 0 ? (
            <SignalAccordion
              title="Structural and other review"
              signals={structuralAndOther}
              defaultExpanded={false}
            />
          ) : null}

          {failures.length === 0 &&
          gig.length === 0 &&
          legitimacy.length === 0 &&
          structuralAndOther.length === 0 &&
          reviewItems.length > 0 ? (
            <SignalAccordion
              title="Review items"
              signals={reviewItems}
              defaultExpanded={compact || reviewItems.length <= 2}
            />
          ) : null}
        </>
      ) : (
        <SignalAccordion
          title="Review items"
          signals={bundle.blockingSignals ?? []}
          defaultExpanded={compact || (bundle.blockingSignals?.length ?? 0) <= 2}
        />
      )}

      <SignalAccordion
        title="Advisory signals"
        signals={advisory}
        defaultExpanded={!compact && advisory.length > 0 && advisory.length <= 2}
      />
    </Stack>
  );
}
